import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, FormControlLabel, Checkbox, Divider } from '@material-ui/core';

import ConfirmPopup from '../common/confirm-popup';

const useStyles = makeStyles((theme) => ({
  body: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    textAlign: 'left',
    gap: `${theme.spacing(1)}px`,
  },
  dupBlock: {
    marginTop: theme.spacing(2),
  },
  dupList: {
    margin: 0,
    paddingLeft: theme.spacing(3),
  },
}));

const MergeConfirmPopup = ({
  open,
  onClose,
  onConfirm,
  propertyName,
  source,
  target,
  duplicates,
  mergeDupsChecked,
  onToggleMergeDups,
}) => {
  const classes = useStyles();

  const buttons = [
    {
      text: 'Cancel',
      props: { color: 'primary', onClick: onClose },
    },
    {
      text: 'Merge',
      props: { variant: 'contained', color: 'secondary', onClick: onConfirm },
    },
  ];

  const hasDuplicates = duplicates.length > 0;

  return (
    <ConfirmPopup open={open} onClose={onClose} buttons={buttons}>
      <div className={classes.body}>
        <Typography variant="h6">Confirm merge</Typography>
        <Typography>Property: {propertyName}</Typography>
        <Typography>
          Merge: {source.name} (id {source.id})
        </Typography>
        <Typography>
          into → {target.name} (id {target.id})
        </Typography>
        <Typography>{source.itemCount} items will be moved to the target.</Typography>

        {hasDuplicates && (
          <div className={classes.dupBlock}>
            <Divider />
            <Typography color="error">
              {duplicates.length} other validated &apos;{target.name}&apos; values exist:
            </Typography>
            <ul className={classes.dupList}>
              {duplicates.map((dup) => (
                <li key={dup.id}>
                  <Typography component="span">
                    {dup.name} (id {dup.id}) — {dup.itemCount} items
                  </Typography>
                </li>
              ))}
            </ul>
            <FormControlLabel
              control={
                <Checkbox
                  checked={mergeDupsChecked}
                  onChange={onToggleMergeDups}
                  color="secondary"
                />
              }
              label={`Also merge these ${duplicates.length} into the target (collapses to 1 canonical record)`}
            />
          </div>
        )}
      </div>
    </ConfirmPopup>
  );
};

MergeConfirmPopup.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  propertyName: PropTypes.string.isRequired,
  source: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    itemCount: PropTypes.number.isRequired,
  }).isRequired,
  target: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  duplicates: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      itemCount: PropTypes.number.isRequired,
    }),
  ),
  mergeDupsChecked: PropTypes.bool,
  onToggleMergeDups: PropTypes.func,
};

MergeConfirmPopup.defaultProps = {
  duplicates: [],
  mergeDupsChecked: false,
  onToggleMergeDups: () => {},
};

export default MergeConfirmPopup;
